import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import Actions from "../../../components/controls/Actions";
import Errors, { getString } from "../../../components/errors/Errors";
import CreatedModifiedInfo from "../../../components/forms/CreatedModifiedInfo";
import FormData from "../../../components/forms/FormData";
import FormLabel from "../../../components/forms/FormLabel";
import FormTable from "../../../components/forms/FormTable";
import FormTr from "../../../components/forms/FormTr";
import { DefaultPagination, getPagination, Pagination } from "../../../components/listview/Pagination";
import ReferencedItemsView from "../../../components/listview/ReferencedItemsView";
import Title from "../../../components/Title";
import useAbortController from "../../../hooks/useAbortController";
import useGlobalState from "../../../hooks/useGlobalState";
import useStatusBarState from "../../../hooks/useStatusBar";
import { Conventions } from "../../../listsSettings/conventions";
import { ErrorCode, IAction, SearchResult, UserPermissions } from "../../../services/types";
import { getCloseAction, getCurrentUrlAsSource, getSelectedItems, getSourceFromUrl } from "../../../services/utils";
import { getUserPermissions } from "../../../services/webapi";
import strings from "../../../strings";
import Add from "../../../svg/Add.svg?react";
import Bin from "../../../svg/Bin.svg?react";
import Edit from "../../../svg/Edit.svg?react";
import * as ConventionFields from "../conventions/Fields";
import { ConventionFull } from "../conventions/types";
import { editForm as editFormConventions, newForm as newFormConventions } from "../conventions/urls";
import { deleteConventions } from "../conventions/webapi";
import * as Fields from "./Fields";
import { CategoryFull } from "./types";
import { allItems, editForm } from "./urls";
import { deleteCategory, getCategory, getCategoryConventions, getCategoryConventionsFilters } from "./webapi";

const DispForm: FC = () => {
  const setPage = useGlobalState((state) => state.setPage);
  const setListName = useGlobalState((state) => state.setListName);
  const [abortController, resetAbortController] = useAbortController();
  const [state, setState] = useState<CategoryFull>();
  const [conventions, setConventions] = useState<SearchResult<ConventionFull> | null>(null);
  const [errorCode, setErrorCode] = useState<ErrorCode>();
  const [permissions, setPermissions] = useState<UserPermissions>();
  const [conventionPermissions, setConventionPermissions] = useState<UserPermissions>();
  const statusBar = useStatusBarState();
  const navigateTo = useNavigate();
  const { id } = useParams<{ id?: string }>();

  useEffect(() => {
    id && initializeState(Number(id));

    return () => {
      abortController && abortController.abort();
    };
  }, [id]);

  const initializeState = async (id: number) => {
    const abortController = resetAbortController();
    const permissions = await getUserPermissions(
      Conventions.Lists.Categories.InternalName,
      Conventions.InternalName,
      abortController.signal,
    );
    if (!abortController.signal.aborted) {
      setPermissions(permissions);
      const canRead = permissions.schemaPermission && permissions.listPermissions.includes("read-all");
      !canRead && setErrorCode(403);
      if (canRead) {
        const [contactPermissions, result] = await Promise.all([
          getUserPermissions(Conventions.Lists.Contacts.InternalName, Conventions.InternalName, abortController.signal),
          getCategory(id, abortController.signal),
        ]);
        if (!abortController.signal.aborted) {
          result.error && setErrorCode(result.error.code);

          if (result.data) {
            setConventionPermissions(contactPermissions);
            setState(result.data);
            loadConventions(result.data.id, pagination, abortController);
          }
        }
      }
    }
  };

  const onDelete = async () => {
    if (state && confirm(strings.confirmDeleteItem)) {
      const abortController = resetAbortController();
      const error = await deleteCategory(state.id, abortController.signal);
      if (!abortController.signal.aborted) {
        error && statusBar.addError(getString(error));
        !error && navigateTo(getSourceFromUrl() ?? allItems);
      }
    }
  };

  const editAction: IAction = {
    title: strings.edit,
    icon: Edit,

    onClick: () => state && navigateTo(`${editForm}/${state.id}?${getCurrentUrlAsSource()}`),
    disabled: !permissions || !permissions.listPermissions.includes("edit-all"),
  };
  const closeAction: IAction = getCloseAction(navigateTo, allItems);
  const deleteAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: onDelete,
    disabled: !permissions || !permissions.listPermissions.includes("delete-all"),
  };

  useEffect(() => {
    if (state) {
      setListName(Conventions.Lists.Categories.Title);
      setPage(state.name);
    }
  }, [state]);

  const listviewId = "category-conventions";

  const pagination = getPagination(listviewId) || {
    ...DefaultPagination,
  };

  const loadConventions = async (categoryId: number, pagination: Pagination, abortController: AbortController) => {
    const result = await getCategoryConventions(categoryId, pagination, abortController.signal);
    if (!abortController.signal.aborted) {
      result.error && statusBar.addError(getString(result.error));
      result.data && setConventions(result.data);
    }
  };

  const addConventionAction: IAction = {
    title: strings.new,
    icon: Add,
    onClick: () => navigateTo(`${newFormConventions}?categoryId=${state?.id}&${getCurrentUrlAsSource()}`),
    disabled:
      !conventionPermissions?.listPermissions.includes("create") || !permissions?.listPermissions.includes("read-all"),
  };

  const editConventionAction: IAction = {
    title: strings.edit,
    icon: Edit,
    onClick: () => {
      const selectedItem = getSelectedItems(conventions)[0];
      selectedItem && navigateTo(`${editFormConventions}/${selectedItem.id}?${getCurrentUrlAsSource()}`);
    },
    disabled: getSelectedItems(conventions).length !== 1 || !permissions?.listPermissions.includes("edit-all"),
  };

  const deleteConventionAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: async () => {
      if (state) {
        const selectedItems = getSelectedItems(conventions);
        if (selectedItems.length > 0 && confirm(strings.confirmDeleteItems)) {
          const abortController = resetAbortController();
          const result = await deleteConventions(
            selectedItems.map((x) => x.id),
            abortController.signal,
          );
          if (!abortController.signal.aborted) {
            if (result.error) {
              statusBar.addError(getString(result.error));
            } else if (result.data) {
              const s = selectedItems.filter((x) => x.id && result.data?.[x.id]);

              if (s.length != selectedItems.length) {
                statusBar.addWarning(strings.notAllElementsDeleted);
              }
              if (s.length > 0) {
                await loadConventions(state.id, pagination, abortController);
              }
            }
          }
        }
      }
    },
    disabled: getSelectedItems(conventions).length === 0 || !permissions?.listPermissions.includes("delete-all"),
  };

  const actions = [addConventionAction, editConventionAction, deleteConventionAction];

  return (
    <>
      {!errorCode && state && (
        <>
          <Title text={"Kategorie"} />
          <Actions actions={[editAction, closeAction, deleteAction]} />
          <FormTable>
            <FormTr>
              <FormLabel>{Fields.name.title}</FormLabel>
              <FormData disp>{state?.name}</FormData>
            </FormTr>
          </FormTable>
          <CreatedModifiedInfo info={state} />

          <ReferencedItemsView
            itemId={state.id}
            id={listviewId}
            actions={actions}
            title={Conventions.Lists.Conventions.Title}
            fields={[ConventionFields.title]}
            allowSelectRows
            reload={(categoryId, pagination) => {
              const abortController = resetAbortController();
              return loadConventions(categoryId, pagination, abortController);
            }}
            searchResult={conventions}
            getFilters={getCategoryConventionsFilters}
            pagination={pagination}
            updateItems={(items) =>
              conventions &&
              setConventions({
                ...conventions,
                items: items,
              })
            }
          />
        </>
      )}
      {errorCode && <Errors errorCode={errorCode} />}
    </>
  );
};

export default DispForm;
